import {
  APIAttachment,
  APIComponentAttributes,
  APIComponentType,
  APIContributor,
  APIDiffBranchToBranch,
  APIEntityType,
  APIFieldAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APIReferenceType,
  APIScenarioScopeData,
  APITagAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  DateRangeFieldMapType,
  DiffMode,
  DirectedTriple,
  FEParentChildReferenceAttributes,
  FormattingOperator,
  LabeledValue,
  LinkedComponent,
  QueryBuilderQuery,
  QueryBuilderRuleValue,
  ReferenceDirection,
  StoredQueryModel,
  TypeId,
  FieldFormattingVersion,
  APIOrganizationUser,
  FilterInterfaceFilter,
  FormattingFilter,
  WithTransactionId,
  ComponentPath,
  PathCollapsingRule,
} from '@ardoq/api-types';

// Views use a numeric sort order, and the numeric value is expected
// by the backend.
export enum NumericSortOrder {
  ASC = 0,
  DESC = 1,
  ON_REFERENCE_ASCENDING = 2,
  ON_REFERENCE_DESCENDING = 3,
}

export interface ContextSort {
  attr: string;
  name?: string;
  order: NumericSortOrder;
  /**
   * Order unset values like null/undefined last.
   * Useful in situations like the GridEditor where the user sorts by a column
   * and expects to see filled values sorted.
   */
  orderUnsetLast?: boolean;
}

export enum DiffType {
  NONE = 'none',
  PLACEHOLDER = 'placeholder',
  ADDED = 'added',
  REMOVED = 'removed',
  CHANGED = 'changed',
  UNCHANGED = 'unchanged',
}

export enum ArdoqIconCategory {
  Ardoq = 'Ardoq',
  BPMN = 'BPMN',
  EDGY = 'EDGY',
  ArchiMate = 'ArchiMate',
  FontAwesome = 'Font Awesome',
  Solution = 'Solution',
}

export type ArdoqIcon = {
  id: string;
  label: string;
  category: ArdoqIconCategory;
  isSVG?: boolean;
  scale?: { x: number; y: number };
};

export type ArdoqShape = {
  id: string;
  label: string;
  category?: ArdoqIconCategory;
  template?: string;
};

export type HierarchyDefinition = {
  paths: DirectedTriple[][] | null;
  startSetResult: string[];
  // TODO we probably don't need both, paths and collapsed paths, we should
  // rather ensure that the consuming code doesn't have to know the difference.
  collapsedPaths: DirectedTriple[][];
  pathResults: ComponentPath[][];
  hasActiveCollapsingRules: boolean;
};

export type LoadedGraph = {
  scopeComponentIds: ArdoqId[];
  scopeReferenceIds: ArdoqId[];
  parentChildReferences: FEParentChildReferenceAttributes[];
  hierarchyDefinition: HierarchyDefinition[];
  /**
   * The enhanced scope data for this loaded graph. In viewpoint mode this is the
   * source of truth; the model interfaces read from it for as long as they're in
   * use (they're a transitional construct for the backbone → reactive-streams
   * migration, meant to disappear).
   */
  scopeData: EnhancedScopeData;
};

export type LoadedGraphWithViewpointMode = LoadedGraph & {
  isViewpointMode: boolean;
};

export enum Branch {
  MAIN = 'main',
  BRANCH = 'branch',
  BRANCH_OFF = 'branchOff',
  SOURCE = 'sourceBranch',
  TARGET = 'targetBranch',
}

export type EnhancedScopeDataWithBranchName = EnhancedScopeData & {
  readonly branchName: Branch;
};

export type HierarchyOfLinkedComponentsWithDirection = {
  componentId: string;
  linkedComponents: HierarchyOfLinkedComponentsWithDirection[];
  /**
   * Each component in the hierarchy has just one parent, but there could be
   * multiple references to the parent based on the paths we found in the graph.
   */
  parentConnections: {
    referenceId: string;
    direction: ReferenceDirection;
    isParentChildReference: boolean;
  }[];
};

export type AggregatedReferenceType = {
  typeIds: ArdoqId[];
  name: string;
};

export type AttributeKey = string;

export type GraphModelMap = Map<ArdoqId, LinkedComponent[]>;

export type ReferenceAdjacency = {
  sourceMap: GraphModelMap;
  targetMap: GraphModelMap;
};

export type ChildrenByParent = Map<ArdoqId, APIComponentAttributes[]>;
export type ComponentsById = Record<ArdoqId, APIComponentAttributes>;

export type ReferenceGraphData = ReferenceAdjacency & {
  referenceMap: Map<ArdoqId, SimpleReferenceModel>;
  referenceTypes: AggregatedReferenceType[];
};

export type ComponentGraphData = {
  childrenByParent: ChildrenByParent;
  componentsById: ComponentsById;
};

export type GraphModelShape = ReferenceGraphData & ComponentGraphData;

export type SimpleReferenceModel = {
  sourceId: ArdoqId;
  targetId: ArdoqId;
};

export type ComponentTypeWithoutChildren = Omit<APIComponentType, 'children'>;

export type GlobalReferenceTypeId = string;

export type GlobalReferenceTypes = {
  targetReferenceTypes: GlobalReferenceType[];
  sourceReferenceTypes: GlobalReferenceType[];
};

export type GlobalReferenceType = {
  name: string;
  id: GlobalReferenceTypeId;
};

export type ImageSrc = string;

export type RepresentationData = {
  isImage: boolean;
  value: ImageSrc | null;
  icon?: ArdoqIcon;
  shapeName?: string | null;
};

export type EnhancedRepresentationData = {
  isImage: boolean;
  value: ImageSrc | null;
  icon: ArdoqIcon;
  color: string;
  contrastColor: string;
};

export type GetCssClassNamesOption = {
  /** if true, returns a lightened version the color, suitable for displaying behind dark text.  */
  useAsBackgroundStyle: boolean;
  /** if true, items lacking an assigned color individually and on their metamodel types will be assigned color based on the depth of their metamodel type in the metamodel parent-child type hierarchy. */
  includeLevels?: boolean;
  /** if true, conditionally formatted items will not be lightened according to {@link GetCssClassNamesOption.useAsBackgroundStyle}. */
  isNewConditionalFormatting?: boolean;
};

export type ReferenceDescriptor = {
  referenceId: string;
  sourceId: string;
  targetId: string;
  referenceTypeId?: string;
  referenceTypeName: string;
};

export interface ParameterOption extends LabeledValue<string> {
  type: string;
}

type ComponentOrReferenceId = ArdoqId;

export interface DynamicFilterParams {
  [parameter: string]: any;
}

export interface DynamicFilterId {
  id: DynamicFilterState['id'];
}

export interface DynamicFilterDefinition {
  id: DynamicFilterState['id'];
  params: DynamicFilterState['params'];
}

export interface DynamicFilterIncludes {
  id: DynamicFilterState['id'];
  references: DynamicFilterState['references'];
  components: DynamicFilterState['components'];
}

export enum DynamicFilterSelectState {
  ACTIVE,
  INACTIVE,
  LOADING,
}

export interface DynamicFilterState {
  id: string;
  components: Set<ComponentOrReferenceId>;
  references: Set<ComponentOrReferenceId>;
  state: DynamicFilterSelectState;
  params: DynamicFilterParams;
  error?: string;
}

export interface CompleteDynamicFilterModel extends StoredQueryModel {
  filterState: DynamicFilterState;
  options: ParameterOption[];
}

export type OnViewSettingsUpdate = (
  viewId: string,
  settings: {
    [x: string]: any;
  },
  persistent: boolean,
  settingsPath?: string[]
) => void;

export type LoadedScenarioData = {
  scenarioData: APIScenarioScopeData | null;
  scenarioRelatedGraph: GraphModelShape | null;
  contextComponents: Set<ArdoqId>;
};
export type ContextShape = {
  componentId: ArdoqId;
  referenceId: ArdoqId;
  workspaceId: ArdoqId;
  scenarioId: ArdoqId;
  workspaceIds: ArdoqId[];
  presentationId: ArdoqId;
  isExploreMode: boolean;
  sort: ContextSort;
};

export type StubComponent = Pick<
  APIComponentAttributes,
  '_id' | 'name' | 'type' | 'component-key'
>;

export type StubWorkspace = Pick<
  APIWorkspaceAttributes,
  '_id' | 'name' | 'componentModel'
>;

export type ShowRelatedComponentsShape = { componentIds: ArdoqId[] };

export type StubReferenceWithTypeName = Pick<
  APIReferenceAttributes,
  '_id' | 'displayText' | 'source' | 'target'
> & { typeName: string };

export type StubTag = Pick<APITagAttributes, '_id' | 'name' | 'rootWorkspace'>;

export type WorkspaceHierarchy = Map<ArdoqId | null, ArdoqId[]>;
export type Hierarchy = Map<ArdoqId, WorkspaceHierarchy>;

export type ComponentTypesById = Record<ArdoqId, APIComponentType>;
type ReferenceTypesById = Record<ArdoqId, APIReferenceType>;

export type ComponentTypes = {
  componentTypes: TypeId[];
  componentTypesById: ComponentTypesById;
};
export type ReferenceTypes = {
  referenceTypes: TypeId[];
  referenceTypesById: ReferenceTypesById;
};
export type ModelTypes = ComponentTypes & ReferenceTypes;

export type ModelEntityTypePair = {
  typeId: TypeId;
  modelId: ArdoqId;
};

export type WorkspaceAndModelLookup = {
  workspacesById: Record<ArdoqId, APIWorkspaceAttributes>;
  modelsById: Record<ArdoqId, APIModelAttributes>;
};

/**
 * Main type-definition of the enhanced scope data.
 * The main difference to scopeData is:
 * - indexes for various entities
 * - more properties are guaranteed to be defined
 *
 * TODO: sseppola add permissions
 */
/**
 * A virtual (collapsed-path) reference produced by path collapsing. It is a
 * display-only stand-in for a chain of real references and never hits the API.
 * Carried into the enhanced scope data (via `enhance`) and, in the legacy path,
 * registered on the backbone `virtualModel`.
 */
export type VirtualReference = {
  sourceId: ArdoqId;
  targetId: ArdoqId;
  referenceId: ArdoqId;
  referenceStyle: PathCollapsingRule['referenceStyle'];
  name: string;
  referenceTypeId: string;
  /** Workspace of the source component (the reference's rootWorkspace). */
  rootWorkspace: ArdoqId;
  /** Workspace of the target component. */
  targetWorkspace: ArdoqId;
};

export type EnhancedScopeData = WorkspaceAndModelLookup & {
  // Shared with APIScopeData
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
  models: APIModelAttributes[];
  workspaces: APIWorkspaceAttributes[];
  fields: APIFieldAttributes[];
  attachments: APIAttachment[];
  // Overwrites APIScopeData (optional -> non-optional)
  tags: APITagAttributes[];
  dateRangeFieldMap: DateRangeFieldMapType;
  users: APIContributor[];
  organizationUsers: APIOrganizationUser[];
  // Custom to EnhancedScopeData
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  referencesById: Record<ArdoqId, APIReferenceAttributes>;
  fieldsById: Record<ArdoqId, APIFieldAttributes>;
  workspacesByModelId: Record<ArdoqId, APIWorkspaceAttributes | undefined>;
  tagsById: Record<ArdoqId, APITagAttributes>;
  typesByModelId: Record<ArdoqId, ModelTypes>;
  fieldsByModelIdAndTypeId: Record<string, Record<string, APIFieldAttributes>>;
  graphData: GraphModelShape;
  hierarchy: Hierarchy;
  fieldLabelByFieldName: Record<string, string>;
  calculatedFields: Set<string>;
  scopeComponentIds: Set<ArdoqId>;
};

export type LabelEntityTypes = 'component' | 'reference';
export type LabelFormattingInfo = {
  type: LabelEntityTypes;
  typeName: string | null;
  fields: string[];
  showFieldName: boolean;
  __id: string;
};

export type TextAreaFormat = 'raw' | 'text' | 'html';

export enum ConditionalFormattingState {
  /** there is no conditional formatting active */
  NORMAL,
  /** conditional formatting is coloring the component */
  FORMATTED,
  /** conditional formatting is active, but not matching the component */
  DIM,
}

export type FormattingRule =
  | TagFormattingRule
  | ComponentOrReferenceFormattingRule;

type FormattingRuleBase = {
  __id: string;
  isInverted: boolean;
  isCollapsed?: boolean;
  color: string;
  version?: FieldFormattingVersion;
};

export type TagFormattingRule = FormattingRuleBase & {
  type: APIEntityType.TAG;
  value: string | undefined;
};

export type CompleteTagFormattingRule = FormattingRuleBase & {
  type: APIEntityType.TAG;
  value: string;
};

export type CompleteFormattingRuleValue = Exclude<QueryBuilderRuleValue, null>;

export type ComponentOrReferenceFormattingRuleBase = FormattingRuleBase & {
  type: APIEntityType.COMPONENT | APIEntityType.REFERENCE;
  field: string | null;
  comparator: FormattingOperator;
  value: QueryBuilderRuleValue;
  typeNames: string[] | null;
};

export type ComponentOrReferenceFormattingRule =
  ComponentOrReferenceFormattingRuleBase & {
    value: CompleteFormattingRuleValue | null;
  };
export type CompleteComponentOrReferenceFormattingRule =
  ComponentOrReferenceFormattingRuleBase & {
    value: CompleteFormattingRuleValue;
  };

export type OpenScenarioClickSource =
  | 'test'
  | 'assetManager'
  | 'directURLVisit'
  | 'addToScenarioDialog'
  | 'presentationSlide'
  | 'gridEditor'
  | 'reload'
  | 'fieldUsage';

export interface PayloadOpenScenario {
  scenarioId: ArdoqId;
  trackingClickSource: OpenScenarioClickSource;
  skipLoadingBar?: boolean;
  workspaceId?: ArdoqId;
  componentId?: ArdoqId;
  isPresentationSlide?: boolean;
}

export type PayloadReloadScenario = Omit<
  PayloadOpenScenario,
  'trackingClickSource'
>;

export interface PayloadScenarioError {
  errorMessage: string;
}

export interface PayloadActiveScenarioId {
  scenarioId: ArdoqId | null;
}

export interface MaybeIsScenarioMode {
  isScenarioMode: boolean;
}

export interface IsInDiffMode {
  isInDiffMode: true;
  activeDiffMode: DiffMode;
}

export interface IsNotInDiffMode {
  isInDiffMode: false;
  activeDiffMode: DiffMode;
}
export type DiffModeState = IsInDiffMode | IsNotInDiffMode;

export type ActiveScenarioState = DiffModeState & {
  scenarioId: ArdoqId;
  isScenarioMode: true;
};

export type InactiveScenarioState = IsNotInDiffMode & {
  scenarioId: null;
  isScenarioMode: false;
};

/**
 * Union type for active and inactive scenario states.
 * This is used to represent if there is an opened scenario or not.
 *
 * @see ActiveScenarioState
 * @see InactiveScenarioState
 */
export type ScenarioModeState = ActiveScenarioState | InactiveScenarioState;

export interface PayloadScenarioIsInSync {
  isInSync: boolean;
}

export interface PayloadOpenAddToScenarioDialog {
  context: CurrentContext;
  componentIds?: ArdoqId[];
  searchQuery?: QueryBuilderQuery;
  addToExistingScenario?: boolean;
  loadedGraph?: LoadedGraphWithViewpointMode;
}

export interface PayloadLoadDiff {
  scenarioId: ArdoqId | null;
}

export type ScopedDiff = {
  branchComponents: APIComponentAttributes[];
  masterComponents: APIComponentAttributes[];
  diffComponents: APIComponentAttributes[];
  branchReferences: APIReferenceAttributes[];
  masterReferences: APIReferenceAttributes[];
  diffReferences: APIReferenceAttributes[];
};

export interface PayloadUpdateScopeDiff {
  scopeDiff: ScopedDiff | null;
}

export type ScopeDiffState = PayloadUpdateScopeDiff & { isLoading: boolean };

export interface PayloadLoadedScenarioData {
  scenarioData: APIScenarioScopeData | null;
}

export interface PayloadUpdateLoadedScenarioData {
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
}

export interface PayloadDiffModeUpdated {
  activeDiffMode: DiffMode;
}

export interface PayloadUpdateEnhancedScopeDiffData {
  branch: EnhancedScopeDataWithBranchName | null;
  main: EnhancedScopeDataWithBranchName | null;
  branchOff: EnhancedScopeDataWithBranchName | null;
  diffData: APIDiffBranchToBranch | null;
}

export type ScopeSize = {
  componentsCount: number;
  referencesCount: number;
  workspacesCount: number;
  tagsCount: number;
  modelsCount: number;
  componentTypesCount: number;
  referenceTypesCount: number;
  fieldsCount: number;
};

export type CurrentContext =
  | {
      workspaceId: ArdoqId;
      componentId: ArdoqId | undefined;
    }
  | undefined;

export type CopyComponentsArgs = {
  targetWorkspace: ArdoqId;
  parent?: ArdoqId;
  componentIds: ArdoqId[];
};

export type AttachmentConflict = {
  attachmentId: ArdoqId;
  filename: string;
  sourceWorkspaceId: ArdoqId;
};

export type TrackingLocation =
  | 'assetsManager'
  | 'assetsManagerModal'
  | 'search'
  | 'scopifiedSearch'
  | 'presentationSlide'
  | 'copyWorkspaceDialog'
  | 'metamodel'
  | 'reportReader'
  | 'organizationSettings'
  | 'directURLVisit'
  | 'componentSelection'
  | 'import'
  | 'optionsMenuSidebar'
  | 'pagesViewBreadcrumb'
  | 'contextMenu'
  | 'traversal'
  | 'createInferenceRule'
  | 'surveyBuilder'
  | 'viewpointNavigator'
  | 'componentDrawerTripleToggler'
  | 'inventory'
  | 'detailsSidebar'
  | 'createComponentDialog'
  | 'browserNavigation'
  | 'homePage';

export enum TrackingPlaceInApp {
  VIEW_PANE_HEADER = 'View pane header0',
  VIEW_SETTINGS_PANEL = 'View settings panel0',
  SAVE_AS_VIEWPOINT_PANEL = 'Save as viewpoint panel0',
  RIGHT_PANE = 'Right pane0',
  RIGHT_PANE_TOOLBAR = 'Right pane toolbar0',
}

export type ComponentIdAndTypeName = { id: ArdoqId; typeName: string };

export type FormattingAndLabelRules = WithTransactionId & {
  rules: (FilterInterfaceFilter | FormattingFilter)[];
};
